/**
 * REVISIÓN DE SEGURIDAD ELÉCTRICA — la visita guiada y el cuadro de cargas.
 * =============================================================================
 *
 * Segundo módulo del levantamiento guiado, y el que sostiene el servicio que hoy es el
 * 100% del ingreso previsto.
 *
 * **Dónde se juega el Principio 7 aquí:** el operador no sabe watts, ni calibres, ni
 * qué exige la norma. Lo único que hace en esta pantalla es CONTAR APARATOS y escribir
 * a cuántos metros están. Todo lo demás —la corriente de cada uno, el circuito que
 * exige, el calibre por ampacidad y por caída, el tope de protección del art. 240-4(d),
 * el 125% de los motores y el balanceo entre fases— lo pone el motor.
 *
 * Y todo sale con su justificación al lado, que es el Principio 9: si el cliente
 * pregunta por qué ese calibre y no uno más barato, la respuesta está a un toque.
 */

import { useState, useMemo } from 'react'
import PasoGuia, { Avance } from './PasoGuia.jsx'
import Sello from './Sello.jsx'
import Materiales from './Materiales.jsx'
import { GUIA_ELECTRICA } from '../motor/guia.js'
import {
  APARATOS, aparatoPorId, corrienteDe, calcularInstalacion,
  CIRCUITOS_MINIMOS, VA_ALUMBRADO_M2, LIMITE_CAIDA, tuboPara
} from '../motor/electrico.js'

const ZONAS = [...new Set(APARATOS.map(a => a.zona))]

export default function Electrico ({ atras, onGuardar, insumos = [], preferencias = {} }) {
  const [i, setI] = useState(0)

  // Lo observable, tal como se captura en campo.
  const [fases, setFases] = useState(2)
  const [principal, setPrincipal] = useState('')
  const [espacios, setEspacios] = useState('')
  const [libres, setLibres] = useState('')
  const [tierraSeparada, setTierraSeparada] = useState(null)
  const [m2, setM2] = useState('')
  const [ambiente, setAmbiente] = useState(30)
  const [cuenta, setCuenta] = useState({})     // { idAparato: cantidad }
  const [distancia, setDistancia] = useState({})
  const [hallazgos, setHallazgos] = useState('')

  const paso = GUIA_ELECTRICA.pasos[i]

  const aparatos = useMemo(
    () => Object.entries(cuenta).filter(([, n]) => n > 0)
      /* `metros` va NULO cuando nadie lo midió, en vez de rellenarse con 20 aquí.
         El motor necesita distinguir medido de supuesto: la canalización domina la mano
         de obra del rubro, y un supuesto no puede verse igual que una medición. */
      .map(([id, n]) => ({ id, cantidad: n, metros: distancia[id] || null })),
    [cuenta, distancia]
  )

  const r = useMemo(() => {
    if (!m2 || !aparatos.length) return null
    return calcularInstalacion({
      m2: parseFloat(m2), aparatos, ambienteC: ambiente,
      tension: 127,
      // El principal decide si el método opcional del art. 220-82 es aplicable.
      acometidaA: parseFloat(principal) || null,
      // Catálogo vivo y criterios de obra de Juan.
      insumos,
      aislamiento: preferencias.aislamiento || 'thw',
      holguraObra: preferencias.holguraObra ?? 1.10,
      metrosPorDefecto: preferencias.metrosPorDefecto ?? 20
    })
  }, [m2, aparatos, ambiente, principal, insumos, preferencias.aislamiento, preferencias.holguraObra, preferencias.metrosPorDefecto])

  const falta = (() => {
    if (paso.id === 'acometida' && !principal) return 'Anota el amperaje del interruptor principal.'
    if (paso.id === 'tablero' && !espacios) return 'Anota cuántos espacios tiene el tablero.'
    if (paso.id === 'aparatos' && !aparatos.length) return 'Cuenta al menos un aparato.'
    if (paso.id === 'planta' && !m2) return 'Escribe los metros construidos.'
    return null
  })()

  const mas = (id, d) => setCuenta(c => ({ ...c, [id]: Math.max(0, (c[id] || 0) + d) }))

  return (
    <div className="app">
      <div className="bar">
        <button className="ib ico" onClick={atras} aria-label="Regresar">
          <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
            strokeWidth="1.5" strokeLinecap="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <h1>Revisión eléctrica
          <span className="crumb">{GUIA_ELECTRICA.duracion} · paso {i + 1} de {GUIA_ELECTRICA.pasos.length}</span>
        </h1>
      </div>

      <main>
        <Avance pasos={GUIA_ELECTRICA.pasos} i={i} onIr={setI} />
        <PasoGuia key={paso.id} paso={paso} />

        {/* ---------- Captura por paso ---------- */}

        {paso.id === 'acometida' && (
          <div className="captura">
            <div className="sec" style={{ margin: '0 0 var(--e4)' }}>¿Cuántas fases llegan?</div>
            <div className="etapas" style={{ marginBottom: 'var(--e5)' }}>
              <button className={`et ${fases === 1 ? 'on1' : ''}`} onClick={() => setFases(1)}>
                Una fase · 127 V
              </button>
              <button className={`et ${fases === 2 ? 'on1' : ''}`} onClick={() => setFases(2)}>
                Dos fases · 127/220 V
              </button>
            </div>
            <label className="f" style={{ marginBottom: 0 }}>
              <span>Amperaje del interruptor principal</span>
              <input type="number" inputMode="numeric" value={principal} placeholder="Ej. 60"
                onChange={e => setPrincipal(e.target.value)} />
            </label>
            {fases === 1 && (
              <div className="warn leible" style={{ marginTop: 'var(--e4)' }}>
                <b>Con una sola fase no hay 220 V.</b> Secadora eléctrica, parrilla de
                inducción, calentador de paso y cargador de auto <b>no se pueden alimentar</b>.
                Si el cliente los quiere, hay que tramitar la segunda fase con CFE —y eso se
                cotiza aparte, nunca se absorbe.
              </div>
            )}
          </div>
        )}

        {paso.id === 'tablero' && (
          <div className="captura">
            <div className="two">
              <label className="f">
                <span>Espacios totales</span>
                <input type="number" inputMode="numeric" value={espacios} placeholder="Ej. 12"
                  onChange={e => setEspacios(e.target.value)} />
              </label>
              <label className="f">
                <span>Espacios libres</span>
                <input type="number" inputMode="numeric" value={libres} placeholder="Ej. 2"
                  onChange={e => setLibres(e.target.value)} />
              </label>
            </div>
            <div className="sec" style={{ margin: '0 0 var(--e3)' }}>
              ¿La barra de tierra está separada de la de neutro?
            </div>
            <div className="etapas">
              <button className={`et ${tierraSeparada === true ? 'on1' : ''}`}
                onClick={() => setTierraSeparada(true)}>Sí, separadas</button>
              <button className={`et ${tierraSeparada === false ? 'on3' : ''}`}
                onClick={() => setTierraSeparada(false)}>No, unidas</button>
              <button className={`et ${tierraSeparada === 'no-hay' ? 'on3' : ''}`}
                onClick={() => setTierraSeparada('no-hay')}>No hay tierra</button>
            </div>
            {tierraSeparada === false && (
              <div className="warn leible" style={{ marginTop: 'var(--e4)' }}>
                <b>Hallazgo grave.</b> Neutro y tierra unidos en un tablero derivado hacen que
                la corriente de retorno circule por la tierra y por las carcasas de los
                aparatos. Fotografíalo: es una partida de corrección, no un detalle.
              </div>
            )}
            {tierraSeparada === 'no-hay' && (
              <div className="warn leible" style={{ marginTop: 'var(--e4)' }}>
                <b>Sin tierra física.</b> Es lo normal en las casas de los sesenta y setenta de
                esta zona, y es exactamente el Paquete Raíz. El art. 250-130(c) permite
                retrofitear la tierra sin reabrir toda la instalación.
              </div>
            )}
          </div>
        )}

        {paso.id === 'aparatos' && (
          <>
            {ZONAS.map(z => (
              <div key={z}>
                <div className="sec">{z}</div>
                {APARATOS.filter(a => a.zona === z).map(a => {
                  const n = cuenta[a.id] || 0
                  return (
                    <div className={`card tap ${n ? 'con' : ''}`} key={a.id}
                      style={{ cursor: 'default' }}>
                      <div className="grow">
                        <div className="t">{a.nombre}</div>
                        <div className="s">
                          {a.watts} W · {corrienteDe(a).toFixed(1)} A
                          {a.v === 220 && ' · necesita 220 V'}
                          {a.dedicado && ' · circuito propio'}
                          {a.motor && ' · motor, conductor al 125%'}
                        </div>
                      </div>
                      <div className="qty">
                        <button aria-label={`Quitar un ${a.nombre}`} onClick={() => mas(a.id, -1)}>−</button>
                        <span>{n}</span>
                        <button aria-label={`Agregar un ${a.nombre}`} onClick={() => mas(a.id, 1)}>+</button>
                      </div>
                    </div>
                  )
                })}
              </div>
            ))}

            {/* Si hay una fase sola, avisar de lo que se contó y no cabe. */}
            {fases === 1 && aparatos.some(x => aparatoPorId(x.id)?.v === 220) && (
              <div className="warn leible">
                <b>Contaste aparatos de 220 V y la casa tiene una sola fase.</b> No se pueden
                alimentar sin tramitar la segunda fase. Dilo antes de cotizar.
              </div>
            )}
          </>
        )}

        {paso.id === 'planta' && (
          <div className="captura">
            <div className="two">
              <label className="f">
                <span>Metros cuadrados construidos</span>
                <input type="number" inputMode="decimal" value={m2} placeholder="Ej. 150"
                  onChange={e => setM2(e.target.value)} />
                <span>De aquí sale la carga de alumbrado, a {VA_ALUMBRADO_M2} VA por m².</span>
              </label>
              <label className="f">
                <span>Temperatura de la canalización (°C)</span>
                <input type="number" inputMode="numeric" value={ambiente}
                  onChange={e => setAmbiente(parseFloat(e.target.value) || 30)} />
                <span>Por plafón o azotea en esta zona pasa de 40 sin problema, y ahí el
                      conductor pierde capacidad.</span>
              </label>
            </div>

            {aparatos.length > 0 && (
              <>
                <div className="sec" style={{ marginTop: 'var(--e6)' }}>
                  ¿A cuántos metros del tablero queda cada uno?
                </div>
                <p className="s leible" style={{ margin: '0 0 var(--e4)' }}>
                  Es el recorrido por muro, no la línea recta. Si no lo sabes, deja 20 m: el
                  sistema lo marca como estimado.
                </p>
                <div className="lados">
                  {aparatos.map(x => {
                    const ap = aparatoPorId(x.id)
                    return (
                      <div className="lado" key={x.id}>
                        <span className="k" style={{ minWidth: '18ch' }}>{ap.nombre}</span>
                        <input type="number" inputMode="numeric" value={distancia[x.id] ?? 20}
                          onChange={e => setDistancia(d => ({ ...d, [x.id]: parseFloat(e.target.value) || 0 }))}
                          aria-label={`Metros hasta ${ap.nombre}`} />
                        <span className="u">m</span>
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {paso.id === 'medicion' && (
          <div className="captura">
            <label className="f" style={{ marginBottom: 0 }}>
              <span>Hallazgos de la medición</span>
              <textarea rows="4" value={hallazgos} onChange={e => setHallazgos(e.target.value)}
                placeholder="Ej. Aislamiento bajo en el circuito de la cocina. Tablero a 62 °C en la pastilla 4. Sin continuidad de tierra en el contacto de la recámara." />
              <span>Cada hallazgo se convierte después en una partida de corrección, con la
                    medición que la justifica al lado.</span>
            </label>
          </div>
        )}

        {paso.id === 'calcular' && (
          r ? <Resultado r={r} fases={fases} principal={principal}
                         espacios={espacios} libres={libres} onGuardar={onGuardar} />
            : <div className="empty">
                <b>Todavía no se puede calcular.</b>
                <p>Faltan los metros construidos y al menos un aparato contado.</p>
              </div>
        )}
      </main>

      <div className="fab"><div className="in">
        <div className="cuadro">
          <div className="celda">
            <span className="k">Paso</span>
            <span className="v">{i + 1} de {GUIA_ELECTRICA.pasos.length}</span>
          </div>
          {r && (
            <div className="celda oculta-chica">
              <span className="k">Circuitos</span>
              <span className="v">{r.circuitos.length}</span>
            </div>
          )}
          {r && (
            <div className="celda importe">
              <span className="k">Acometida</span>
              <span className="v">{r.corrienteAcometida.toFixed(0)} A</span>
            </div>
          )}
        </div>
        {i > 0 && <button className="gh" onClick={() => setI(i - 1)} aria-label="Paso anterior">←</button>}
        {i < GUIA_ELECTRICA.pasos.length - 1 && (
          <button onClick={() => setI(i + 1)} disabled={!!falta} title={falta || ''}>
            {falta || 'Siguiente'}
          </button>
        )}
      </div></div>
    </div>
  )
}

/* ============================ El resultado ============================ */

function Resultado ({ r, fases, principal, espacios, libres, onGuardar }) {
  const [auditando, setAuditando] = useState(false)
  const excede = principal && r.corrienteAcometida > parseFloat(principal)
  const noCaben = espacios && libres !== '' &&
    r.circuitos.length > parseFloat(libres) + 0

  return (
    <>
      <div className="sec">Lo que necesita esta casa</div>

      {excede && (
        <div className="warn leible">
          <b>La demanda calculada ({r.corrienteAcometida.toFixed(0)} A) supera el interruptor
          principal ({principal} A).</b> Hay que subir la acometida con CFE, y ese trámite se
          cotiza aparte: nunca se absorbe en el precio de la obra.
        </div>
      )}

      <div className="plan total">
        <div className="hd">
          <div className="k">Circuitos</div>
          <div className="v">{r.circuitos.length}</div>
          <div className="d">
            {Math.round(r.demandaTotalVA).toLocaleString('es-MX')} VA de demanda ·
            tablero de {r.espaciosTablero} espacios con 25% de reserva
            {noCaben && ' · no caben en los espacios libres de hoy'}
          </div>
        </div>
      </div>

      {/* Los dos métodos de la norma, a la vista. No es un detalle de auditoría: es el
          argumento con el que se defiende una acometida más chica —y más barata— frente a
          un cliente o frente a otro instalador que propuso de más. */}
      <div className="nota leible" style={{ marginTop: 'var(--e4)' }}>
        <b>Cómo se calculó la acometida.</b> La norma permite dos caminos y aquí se
        recorrieron los dos:
        <ul style={{ margin: 'var(--e3) 0 0', paddingLeft: '1.1rem' }}>
          <li>Método por partes (art. 220, parte C):{' '}
            <b>{Math.round(r.demanda.porPartes).toLocaleString('es-MX')} VA</b></li>
          <li>Método opcional de vivienda (art. 220-82):{' '}
            <b>{Math.round(r.demanda.opcional).toLocaleString('es-MX')} VA</b>
            {!r.demanda.opcionalAplicable && ' — no aplicable aquí'}</li>
        </ul>
        <p style={{ margin: 'var(--e3) 0 0' }}>{r.demanda.porque}</p>
        <p style={{ margin: 'var(--e2) 0 0' }}>{r.demanda.porqueFactorFijos}</p>
      </div>

      {/* ---- Balanceo ---- */}
      {fases === 2 && (
        <>
          <div className="sec">Balanceo entre fases</div>
          <div className="ln"><span>Fase A</span><b>{r.balance.cargaA.toFixed(1)} A</b></div>
          <div className="ln"><span>Fase B</span><b>{r.balance.cargaB.toFixed(1)} A</b></div>
          <div className="ln big">
            <span>Desequilibrio</span>
            <b style={{ color: r.balance.aceptable ? 'var(--verde)' : 'var(--alto)' }}>
              {r.balance.desequilibrio.toFixed(1)}%
            </b>
          </div>
          <p className="s leible" style={{ marginTop: 'var(--e3)' }}>
            {r.balance.aceptable
              ? 'Queda parejo. Una casa desbalanceada desperdicia capacidad contratada y hace ' +
                'que el neutro cargue la diferencia.'
              : 'Conviene reacomodar: por encima del 10% se desperdicia capacidad y el neutro ' +
                'carga la diferencia.'}
          </p>
        </>
      )}

      {/* ---- Cuadro de cargas ---- */}
      <div className="sec">Cuadro de cargas</div>
      <div className="tabla-scroll">
        <table className="d">
          <thead>
            <tr>
              <th>Circuito</th><th className="n">A</th><th>Calibre</th>
              <th className="n">Protección</th><th className="n">Caída</th>
            </tr>
          </thead>
          <tbody>
            {r.circuitos.map((c, n) => (
              <tr key={n}>
                <td>
                  {c.nombre}
                  {c.exigido && <div className="s">Exigido por {c.exigido}</div>}
                  {c.esMotor && <div className="s">Motor: conductor al 125%</div>}
                  {c.razonDimensionado && <div className="s">{c.razonDimensionado}</div>}
                  {auditando && c.calibre && <div className="s">{c.calibre.porque}</div>}
                </td>
                <td className="n">{c.corriente.toFixed(1)}</td>
                <td>{c.calibre ? `${c.calibre.awg} AWG` : '—'}</td>
                <td className="n">{c.calibre ? `${c.calibre.proteccion} A` : '—'}</td>
                <td className="n">
                  {c.calibre?.caida ? `${c.calibre.caida.porciento.toFixed(1)}%` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* El material. Hasta el 2026-08-14 este módulo calculaba calibres impecables y esa
          cadena no llegaba a ningún precio: el levantamiento era una isla. */}
      <div className="sec">Material</div>
      <Materiales lista={r.materiales} auditando={auditando} />

      {/* Cuánto del precio descansa en un supuesto. Va JUNTO al material y no escondido en
          la auditoría, porque la canalización es el renglón más caro de un recableado y
          sale de estas distancias: si no se midieron, el número más grande del presupuesto
          no es una medición. Decirlo también es la mejor palanca de venta que hay — el
          operador sabe exactamente qué ir a medir para cerrar mejor. */}
      {r.metros.circuitosSupuestos > 0 && (
        <div className="warn leible" style={{ marginTop: 'var(--e4)' }}>
          <b>El {Math.round(r.metros.porcentajeSupuesto * 100)}% de la canalización va con
          distancias supuestas.</b> {r.metros.porque} Si vas a volver a la casa, medir esos{' '}
          {r.metros.circuitosSupuestos} tramos es lo que más ajusta este número.
        </div>
      )}

      <button type="button" className="discreto" style={{ marginTop: 'var(--e5)' }}
        onClick={() => setAuditando(!auditando)} aria-expanded={auditando}>
        {auditando ? 'Ocultar de dónde sale cada renglón' : 'Ver de dónde sale cada renglón'}
      </button>

      {auditando && (
        <div className="nota leible" style={{ marginTop: 'var(--e4)' }}>
          <p style={{ marginTop: 0 }}>
            <b>Ampacidad.</b> Se toma de la Tabla 310-15(b)(16) de la NOM-001-SEDE-2012, en la
            columna que permite el art. 110-14: por debajo de 100 A manda la de 60 °C, no la de
            90. Después se corrige por temperatura ambiente y por número de conductores en el
            tubo.
          </p>
          <p>
            <b>Caída de tensión.</b> Se calcula con la impedancia por kilómetro de la Tabla 9,
            no con la resistividad simple —esa ignora la reactancia—. El límite de{' '}
            {LIMITE_CAIDA.derivado}% es <b>criterio de calidad propio</b>: en la norma aparece
            como nota informativa, no como obligación. Manda el criterio más exigente de los dos.
          </p>
          <p>
            <b>Protección.</b> El art. 240-4(d) topa los calibres chicos: el 14 AWG no admite
            más de 15 A y el 12 no pasa de 20, sin importar lo que diga su columna de 90 °C.
          </p>
          <p style={{ marginBottom: 0 }}>
            <b>Motores.</b> El art. 430-22 exige dimensionar su conductor al 125% de la
            corriente a plena carga, porque arrancan tirando varias veces su nominal.
          </p>
        </div>
      )}

      <div className="prov leible" style={{ marginTop: 'var(--e5)' }}>
        <b>Sin calibrar todavía:</b> {r.sinCalibrar.join(', ')}. Todo lo demás está leído del
        texto oficial de la NOM-001-SEDE-2012 publicado por el DOF: ampacidad, corrección por
        temperatura y agrupamiento, impedancia, relleno de tubo, carga por metro cuadrado y
        factores de demanda. Lo único que ninguna norma publica es cuánto consume el aparato
        que está instalado en <i>esta</i> casa: eso se lee de su placa. <Sello />
      </div>

      {/* Sólo aparece cuando el levantamiento se abrió DESDE una habitación: si se abrió
          suelto desde el inicio, no hay a dónde guardarlo y prometerlo sería mentir. */}
      {onGuardar && (
        <button className="ib pri w" style={{ marginTop: 'var(--e6)' }}
          onClick={() => onGuardar(r)}>Agregar a la cotización</button>
      )}
    </>
  )
}
